<template>
  <div
    class="services-map-container"
    :class="{
      'service-stations-showed': showServiceStations && !isMobile,
      'mobile': isMobile,
    }"
  >
    <search-bar
      class="map-search-bar"
      v-if="!isMobile"
    />
    <div
      :class="{ 'mobile': isMobile }"
      id="map"
    />
    <resize-observer @notify="handleResize" />
    <template v-if="showServiceStations || isMobile">
      <div
        class="service-stations-wrapper"
        :class="{ 'mobile': isMobile }"
      >
        <div
          class="service-stations-container"
        >
          <search-bar
            class="map-search-bar mobile"
            v-if="isMobile"
          />
          <add-partner
            v-if="!favouritePartner && withLogin"
            :show-button="false"
          />
          <template v-if="filteredStations.length">
            <service-station-block
              class="map-view"
              :id="`partner-${serviceStation.id}`"
              v-for="(serviceStation, index) in filteredStations"
              v-if="serviceStation.isActive"
              :class="{ 'hovered': hoverIndex === serviceStation.id }"
              :show-details="true"
              :show-fav-label="true"
              :set-favourite="true"
              :key="index"
              :set-order="true"
              @block-mouse-over="mouseOverBlock($event)"
              @block-mouse-leave="mouseLeaveBlock($event)"
              @block-click="onClick($event)"
              :map-view="true"
              :show-page-link="isMobile"
              :service-station="serviceStation"
              :with-slot="true"
            >
              <transition
                name="toggle-button"
                mode="out-in"
              >
                <standard-button
                  class="choose-service-partner-button"
                  classType="secondary"
                  :class="{
                    'show': isMobile || hoverIndex === serviceStation.id,
                  }"
                  text="Pokaż na mapie"
                  v-if="(isMobile || hoverIndex === serviceStation.id) && serviceStation.location.lat && serviceStation.location.lng"
                  @buttonClick="onClick({
                    lat: serviceStation.location.lat,
                    lng: serviceStation.location.lng,
                    partner: serviceStation.id
                  })"
                />
              </transition>
            </service-station-block>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ServiceStationBlock from '~/ServiceStationBlock';
import SearchBar from '~/SearchBar';
import AddPartner from '~/AddPartner';
import StandardButton from '~/StandardButton';
import L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import 'leaflet.markercluster';
import { mapGetters } from 'vuex';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import 'vue-resize/dist/vue-resize.css';
import { ResizeObserver } from 'vue-resize';

const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl,
});

L.Marker.prototype.options.icon = DefaultIcon;

export default {
  components: {
    ServiceStationBlock,
    SearchBar,
    StandardButton,
    AddPartner,
    ResizeObserver,
  },
  data() {
    return {
      map: null,
      tileLayer: null,
      markers: [],
      showServiceStations: true,
      markerLayer: null,
      hoverIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      filteredStations: 'serviceStations/getFiltered',
      buttonColor: 'brand/getThirdColor',
      primaryColor: 'brand/getPrimaryColor',
      favouritePartner: 'user/getFavouritePartner',
      authStatus: 'auth/getAuthStatus',
    }),
    withLogin() {
      return this.authStatus.loggedIn;
    },
    isMobile() {
      return this.$mq === 'mobile' || this.$mq === 'xs';
    },
    center() {
      const lat = [];
      const lng = [];

      this.filteredStations.forEach((station) => {
        lat.push(station.location.lat);
        lng.push(station.location.lng);
      });

      return [
        (lat.reduce((total, curr) => parseFloat(total) + parseFloat(curr)) / lat.length).toFixed(6),
        (lng.reduce((total, curr) => parseFloat(total) + parseFloat(curr)) / lng.length).toFixed(6),
      ];
    },
    initialZoom() {
      return this.isMobile ? 6 : 7;
    },
  },
  methods: {
    handleResize() {
      this.map.invalidateSize(false);
    },
    initializeMap() {
      L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

      this.map = L.map('map', { gestureHandling: true }).setView([51.97, 19.11], this.initialZoom);
      this.tileLayer = L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
        // eslint-disable-next-line
        attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
        maxZoom: 18,
        id: 'mapbox/light-v10',
        accessToken: 'pk.eyJ1IjoibWJ5c3Ryenlja2kiLCJhIjoiY2pveWNiNDBnMmFlNDNwbGhycjU1bmVuMSJ9.gXdBdiCPAa9OOmjTd5y9vA',
        zoomControl: false,
      }).addTo(this.map);

      this.map.zoomControl.setPosition('topright');
    },
    initializeMarkers() {
      let promises = [];
      this.filteredStations.forEach((aso) => {
        if (aso.isActive && aso.location.lat && aso.location.lng) {
          const coords = L.latLng(aso.location.lat, aso.location.lng);
          const icon = L.divIcon({
            className: `map-marker-${aso.id}`,
            iconSize: 50,
            html: `
              <div class="marker-label">
                <div class="marker-label-content">
                  <span class="service-icon">
                  </span>
                </div>
                <div class="info-box">
                  <div>
                    ${aso.name}
                  </div>
                  <div>
                    ${aso.city}
                  </div>
                </div>
              </div>
            `,
          });
          const marker = L.marker(coords, { icon })
            .on('mouseover', (m) => {
              // eslint-disable-next-line
              m.target._icon.className = `${m.target._icon.className} hovered`;
              this.hoverIndex = aso.id;
            })
            .on('mouseout', (m) => {
              // eslint-disable-next-line
              m.target._icon.className = m.target._icon.className.replace(' hovered', '');
              this.hoverIndex = 0;
            })
            .on('click', () => {
              this.map.setView(coords, 15);
              this.showServiceStations = true;

              const partner = document.getElementById(`partner-${aso.id}`);
              setTimeout(() => {
                partner.scrollIntoView();
                window.scrollBy(0, -47);
              }, 0);
            });
          this.markers = [...this.markers, marker];

          promises.push(Promise.resolve(marker));
        }
      });

      this.markerLayer = L.markerClusterGroup({
        showCoverageOnHover: false,
        iconCreateFunction(cluster) {
          return L.divIcon({
            className: 'cluster-icon-wrapper',
            iconSize: 50,
            html: `
              <div class="cluster-icon" style="background-color: black;">
                <span class="cluster-count">
                  ${cluster.getChildCount()}
                </span>
              </div>
            `,
          });
        },
      });
      this.markerLayer.addLayer(L.layerGroup(this.markers));
      this.markerLayer.on('clusterclick', () => {
        this.showServiceStations = true;
      });
      this.map.addLayer(this.markerLayer);

      return Promise.all(promises);
    },
    clearMarkers() {
      this.markers = [];
      this.markerLayer.clearLayers();
    },
    mouseOverBlock(event) {
      const marker = document.getElementsByClassName(`map-marker-${event}`);
      if (marker.length) {
        marker[0].className = `${marker[0].className} hovered`;
      }
      this.hoverIndex = event;
    },
    mouseLeaveBlock(event) {
      const marker = document.getElementsByClassName(`map-marker-${event}`);
      if (marker.length) {
        marker[0].className = marker[0].className.replace(' hovered', '');
      }
      this.hoverIndex = 0;
    },
    onClick(event) {
      if (this.isMobile) {
        window.scrollTo(0, 0);
      }
      this.$router.push({ query: { lat: event.lat, lng: event.lng, partner: event.partner } });
    },
    checkQuery() {
      const { lat, lng } = this.$route.query;
      if (this.$route.query.lat && this.$route.query.lng) {
        this.map.setView([lat, lng], 15);
      }
      if (this.$route.query.partner) {
        setTimeout(() => {
          const partner = document.getElementById(`partner-${this.$route.query.partner}`);
          if (partner) {
            partner.scrollIntoView();
          }
        }, 0);
      }
    },
  },
  watch: {
    filteredStations() {
      this.clearMarkers();
      this.initializeMarkers();
    },
    '$route.query.lat': function () {
      this.map.setView(this.$route.query, 15);
    },
  },
  mounted() {
    this.initializeMap();
    this.initializeMarkers()
      .then(() => {
        this.$nextTick(() => {
          setTimeout(() => this.checkQuery(), 1000);
        });
      });

  },
  destroyed() {
    this.map.remove();
  }
};
</script>
